import React, { useEffect, useState } from 'react';
import { X, Loader2, Users, Clock, MessageSquare, Plus, Trash2, Edit2, Check, Linkedin, Mail, Phone, ChevronRight, Copy, Wand2, Search, ShieldAlert, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchAuth = async (url, opts = {}) => {
  const token = localStorage.getItem('backoffice_token');
  const res = await fetch(url, { ...opts, headers: { ...(opts.headers || {}), Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: Users },
  { id: 'contacts', label: 'Decision Makers', icon: Users },
  { id: 'outreach', label: 'Outreach & Timeline', icon: MessageSquare },
  { id: 'pipeline', label: 'Pipeline', icon: ChevronRight },
  { id: 'messages', label: 'AI Messages', icon: Wand2 },
];

const PIPELINE_STAGES = ['Prospect', 'Contacted', 'Discovery Call', 'Proposal', 'Client'];
const RESPONSE_STATUSES = ['No Response', 'Interested', 'Meeting Scheduled', 'Client', 'Not a Fit'];
const CONFIDENCE_LEVELS = ['High', 'Medium', 'Low'];

const PIPELINE_COLORS = {
  'Prospect': 'bg-gray-100 text-gray-700 border-gray-300',
  'Contacted': 'bg-blue-100 text-blue-700 border-blue-300',
  'Discovery Call': 'bg-amber-100 text-amber-700 border-amber-300',
  'Proposal': 'bg-purple-100 text-purple-700 border-purple-300',
  'Client': 'bg-green-100 text-green-700 border-green-300',
};

const STATUS_COLORS = {
  'No Response': 'text-gray-500',
  'Interested': 'text-blue-600',
  'Meeting Scheduled': 'text-amber-600',
  'Client': 'text-green-600',
  'Not a Fit': 'text-red-500',
};

const CompanyDetailModal = ({ company, onClose }) => {
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [outreachData, setOutreachData] = useState(null);
  const [saving, setSaving] = useState(false);

  // Decision maker form
  const [showAddDM, setShowAddDM] = useState(false);
  const [dmForm, setDmForm] = useState({ contact_name: '', title: '', department: '', linkedin_url: '', email: '', confidence_level: 'Medium' });

  // Outreach note form
  const [noteAction, setNoteAction] = useState('');
  const [noteText, setNoteText] = useState('');

  // AI Messages
  const [aiMessages, setAiMessages] = useState(null);
  const [loadingMessages, setLoadingMessages] = useState(false);

  // Apollo search
  const [apolloResults, setApolloResults] = useState(null);
  const [searchingApollo, setSearchingApollo] = useState(false);
  const [revealingId, setRevealingId] = useState(null);

  // Ambiguity resolver
  const [ambiguityData, setAmbiguityData] = useState(null);
  const [resolvingAmbiguity, setResolvingAmbiguity] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const d = await fetchAuth(`${API_URL}/api/outreach/company/${encodeURIComponent(company)}`);
      setOutreachData(d);
    } catch { setOutreachData({ company, contacted: false, response_status: 'No Response', pipeline_stage: 'Prospect', timeline: [], decision_makers: [] }); }
    // Load ambiguity classification (non-blocking, cache only)
    try {
      const r = await fetchAuth(`${API_URL}/api/intelligence/ambiguity-resolver/lookup/${encodeURIComponent(company)}`);
      // Lookup returns source='none' if not cached, otherwise returns cached data with original source (llm/cache)
      if (r?.source && r.source !== 'none') setAmbiguityData(r);
    } catch {}
    setLoading(false);
  };

  const resolveAmbiguity = async () => {
    setResolvingAmbiguity(true);
    try {
      const d = await fetchAuth(`${API_URL}/api/intelligence/ambiguity-resolver/resolve`, {
        method: 'POST',
        body: JSON.stringify({ company, force: false }),
      });
      if (d.error) { toast.error(d.error); } else {
        setAmbiguityData(d);
        toast.success('Classification complete');
      }
    } catch { toast.error('Classification failed'); }
    setResolvingAmbiguity(false);
  };

  useEffect(() => { loadData(); }, [company]);

  const updateOutreach = async (updates) => {
    setSaving(true);
    try {
      await fetchAuth(`${API_URL}/api/outreach/company/${encodeURIComponent(company)}`, { method: 'PATCH', body: JSON.stringify(updates) });
      toast.success('Updated');
      await loadData();
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const addDecisionMaker = async () => {
    if (!dmForm.contact_name || !dmForm.title) { toast.error('Name and Title are required'); return; }
    setSaving(true);
    try {
      await fetchAuth(`${API_URL}/api/outreach/decision-maker`, { method: 'POST', body: JSON.stringify({ ...dmForm, company }) });
      toast.success('Contact added');
      setDmForm({ contact_name: '', title: '', department: '', linkedin_url: '', email: '', confidence_level: 'Medium' });
      setShowAddDM(false);
      await loadData();
    } catch { toast.error('Failed to add contact'); }
    setSaving(false);
  };

  const deleteDecisionMaker = async (dmId) => {
    try {
      await fetchAuth(`${API_URL}/api/outreach/decision-maker/${dmId}`, { method: 'DELETE' });
      toast.success('Contact removed');
      await loadData();
    } catch { toast.error('Failed to delete'); }
  };

  const addTimelineNote = async () => {
    if (!noteAction) { toast.error('Action is required'); return; }
    try {
      await fetchAuth(`${API_URL}/api/outreach/timeline`, { method: 'POST', body: JSON.stringify({ company, action: noteAction, notes: noteText }) });
      toast.success('Timeline entry added');
      setNoteAction('');
      setNoteText('');
      await loadData();
    } catch { toast.error('Failed to add note'); }
  };

  const generateMessages = async () => {
    setLoadingMessages(true);
    try {
      const d = await fetchAuth(`${API_URL}/api/intelligence/generate-outreach/${encodeURIComponent(company)}`);
      setAiMessages(d);
    } catch { toast.error('Failed to generate messages'); }
    setLoadingMessages(false);
  };

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const logMessageToTimeline = async (type) => {
    try {
      await fetchAuth(`${API_URL}/api/outreach/timeline`, { method: 'POST', body: JSON.stringify({ company, action: `Sent ${type} message`, notes: 'Generated via AI Outreach' }) });
      toast.success('Logged to timeline');
    } catch { toast.error('Failed to log'); }
  };

  const searchApollo = async () => {
    setSearchingApollo(true);
    try {
      const d = await fetchAuth(`${API_URL}/api/intelligence/company/${encodeURIComponent(company)}/decision-makers?limit=10`);
      if (d.error) { toast.error(d.error); } else { setApolloResults(d); }
    } catch { toast.error('Apollo search failed'); }
    setSearchingApollo(false);
  };

  const revealAndAdd = async (contact) => {
    setRevealingId(contact.apollo_id);
    try {
      const revealed = await fetchAuth(`${API_URL}/api/intelligence/apollo/reveal/${contact.apollo_id}`, { method: 'POST' });
      if (revealed.error) { toast.error(revealed.error); setRevealingId(null); return; }
      // Add to decision makers
      await fetchAuth(`${API_URL}/api/outreach/decision-maker`, {
        method: 'POST',
        body: JSON.stringify({
          company,
          contact_name: revealed.contact_name || contact.contact_name,
          title: revealed.title || contact.title,
          department: revealed.department || contact.department || '',
          linkedin_url: revealed.linkedin_url || contact.linkedin_url || '',
          email: revealed.email || contact.email || '',
          confidence_level: 'High',
        }),
      });
      toast.success(`Added ${revealed.contact_name || contact.contact_name}`);
      await loadData();
    } catch { toast.error('Failed to reveal contact'); }
    setRevealingId(null);
  };

  const addApolloContactDirect = async (contact) => {
    setSaving(true);
    try {
      await fetchAuth(`${API_URL}/api/outreach/decision-maker`, {
        method: 'POST',
        body: JSON.stringify({
          company,
          contact_name: contact.contact_name,
          title: contact.title,
          department: contact.department || '',
          linkedin_url: contact.linkedin_url || '',
          email: contact.email || '',
          confidence_level: 'Medium',
        }),
      });
      toast.success(`Added ${contact.contact_name}`);
      await loadData();
    } catch { toast.error('Failed to add'); }
    setSaving(false);
  };

  const dms = outreachData?.decision_makers || [];
  const timeline = outreachData?.timeline || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="company-detail-modal">
        {/* Header */}
        <div className="bg-[#0A1F44] text-white px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold" data-testid="modal-company-name">{company}</h2>
            <div className="flex items-center gap-3 mt-1">
              <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full border ${PIPELINE_COLORS[outreachData?.pipeline_stage] || PIPELINE_COLORS.Prospect}`}>
                {outreachData?.pipeline_stage || 'Prospect'}
              </span>
              <span className={`text-xs ${STATUS_COLORS[outreachData?.response_status] || 'text-gray-400'}`}>
                {outreachData?.response_status || 'No Response'}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-lg" data-testid="close-modal"><X className="h-5 w-5" /></button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6 flex-shrink-0">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-3 text-xs font-semibold border-b-2 transition-all ${
                tab === t.id ? 'border-[#D4AF37] text-[#0A1F44]' : 'border-transparent text-gray-400 hover:text-gray-600'
              }`}>
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
          ) : (
            <>
              {/* Overview Tab */}
              {tab === 'overview' && (
                <div className="space-y-4" data-testid="tab-overview-content">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-gray-50 rounded-lg p-3 text-center border">
                      <p className="text-lg font-bold text-[#0A1F44]">{dms.length}</p>
                      <p className="text-[10px] text-gray-500">Decision Makers</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center border">
                      <p className="text-lg font-bold text-[#0A1F44]">{timeline.length}</p>
                      <p className="text-[10px] text-gray-500">Timeline Entries</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center border">
                      <p className="text-lg font-bold text-[#0A1F44]">{outreachData?.contacted ? 'Yes' : 'No'}</p>
                      <p className="text-[10px] text-gray-500">Contacted</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center border">
                      <p className="text-sm font-bold text-[#0A1F44]">{outreachData?.next_followup_date || '—'}</p>
                      <p className="text-[10px] text-gray-500">Next Follow-up</p>
                    </div>
                  </div>
                  {outreachData?.notes && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-xs font-semibold text-amber-700 mb-1">Notes</p>
                      <p className="text-sm text-gray-700">{outreachData.notes}</p>
                    </div>
                  )}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-xs font-semibold text-blue-700 mb-2">Quick Actions</p>
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => { setTab('contacts'); setShowAddDM(true); }}
                        className="px-3 py-1.5 text-xs font-semibold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0A1F44]/80" data-testid="quick-add-contact">
                        <Plus className="h-3 w-3 inline mr-1" />Add Contact
                      </button>
                      <button onClick={() => setTab('outreach')}
                        className="px-3 py-1.5 text-xs font-semibold bg-[#D4AF37] text-[#0A1F44] rounded-lg hover:bg-[#D4AF37]/80" data-testid="quick-log-outreach">
                        <MessageSquare className="h-3 w-3 inline mr-1" />Log Outreach
                      </button>
                      <button onClick={() => setTab('pipeline')}
                        className="px-3 py-1.5 text-xs font-semibold bg-white text-[#0A1F44] border border-gray-300 rounded-lg hover:bg-gray-50" data-testid="quick-update-pipeline">
                        <ChevronRight className="h-3 w-3 inline mr-1" />Update Pipeline
                      </button>
                    </div>
                  </div>

                  {/* AI Ambiguity Classification */}
                  <div className={`border rounded-lg p-4 ${
                    ambiguityData?.recommended_action === 'exclude' ? 'bg-red-50 border-red-200' :
                    ambiguityData?.recommended_action === 'review' ? 'bg-amber-50 border-amber-200' :
                    ambiguityData?.recommended_action === 'keep' ? 'bg-emerald-50 border-emerald-200' :
                    'bg-slate-50 border-slate-200'
                  }`} data-testid="ambiguity-section">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                        {ambiguityData?.recommended_action === 'exclude' ? <ShieldAlert className="h-3.5 w-3.5 text-red-600" /> :
                         ambiguityData?.recommended_action === 'keep' ? <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> :
                         <AlertTriangle className="h-3.5 w-3.5 text-slate-500" />}
                        AI Classification
                      </p>
                      {!ambiguityData ? (
                        <button onClick={resolveAmbiguity} disabled={resolvingAmbiguity}
                          className="px-2.5 py-1 text-[10px] font-semibold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0A1F44]/80 disabled:opacity-50 flex items-center gap-1"
                          data-testid="classify-btn">
                          {resolvingAmbiguity ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                          Classify
                        </button>
                      ) : (
                        <span className="text-[10px] text-slate-400">via {ambiguityData.model || 'LLM'}</span>
                      )}
                    </div>
                    {ambiguityData ? (
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded border ${
                            ambiguityData.recommended_action === 'exclude' ? 'bg-red-100 text-red-700 border-red-300' :
                            ambiguityData.recommended_action === 'review' ? 'bg-amber-100 text-amber-700 border-amber-300' :
                            'bg-emerald-100 text-emerald-700 border-emerald-300'
                          }`} data-testid="ambiguity-action">{ambiguityData.recommended_action?.toUpperCase()}</span>
                          <span className={`text-[10px] font-medium ${
                            ambiguityData.government_defense_risk === 'confirmed' || ambiguityData.government_defense_risk === 'high' ? 'text-red-600' :
                            ambiguityData.government_defense_risk === 'medium' ? 'text-amber-600' : 'text-emerald-600'
                          }`}>
                            Gov/Defense Risk: {ambiguityData.government_defense_risk}
                          </span>
                          <span className="text-[10px] text-slate-400">
                            Confidence: {Math.round((ambiguityData.confidence || 0) * 100)}%
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-600">{ambiguityData.reasoning}</p>
                        <p className="text-[10px] text-slate-400">Type: {ambiguityData.entity_type?.replace(/_/g, ' ')}</p>
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-400">Click "Classify" to check if this company is a government entity, defense contractor, or viable private-sector target.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Decision Makers Tab */}
              {tab === 'contacts' && (
                <div className="space-y-4" data-testid="tab-contacts-content">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[#0A1F44]">{dms.length} contact{dms.length !== 1 ? 's' : ''}</p>
                    <div className="flex gap-2">
                      <button onClick={searchApollo} disabled={searchingApollo} data-testid="find-dm-btn"
                        className="px-3 py-1.5 text-xs font-semibold bg-[#D4AF37] text-[#0A1F44] rounded-lg hover:bg-[#D4AF37]/80 disabled:opacity-50 flex items-center gap-1">
                        {searchingApollo ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                        Find Decision Makers
                      </button>
                      <button onClick={() => setShowAddDM(!showAddDM)} data-testid="toggle-add-dm"
                        className="px-3 py-1.5 text-xs font-semibold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0A1F44]/80">
                        <Plus className="h-3 w-3 inline mr-1" />{showAddDM ? 'Cancel' : 'Manual Add'}
                      </button>
                    </div>
                  </div>

                  {/* Apollo Search Results */}
                  {apolloResults && (
                    <div className="bg-[#D4AF37]/5 border border-[#D4AF37]/30 rounded-xl p-4 space-y-3" data-testid="apollo-results">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-[#0A1F44]">
                          Apollo Results — {apolloResults.contacts?.length || 0} decision makers found
                        </p>
                        <button onClick={() => setApolloResults(null)} className="text-xs text-gray-400 hover:text-gray-600">Dismiss</button>
                      </div>
                      <div className="space-y-2">
                        {(apolloResults.contacts || []).map((ac, i) => {
                          const alreadyAdded = dms.some(d => d.title === ac.title && d.contact_name.includes(ac.first_name));
                          return (
                            <div key={ac.apollo_id || i} className="bg-white border rounded-lg p-3 flex items-center justify-between" data-testid={`apollo-contact-${i}`}>
                              <div className="flex-1">
                                <p className="text-sm font-semibold text-[#0A1F44]">
                                  {ac.contact_name}
                                  {ac.is_revealed && <span className="ml-1 text-[9px] bg-green-100 text-green-600 px-1 rounded">Revealed</span>}
                                  {!ac.is_revealed && <span className="ml-1 text-[9px] bg-gray-100 text-gray-500 px-1 rounded">Partial</span>}
                                </p>
                                <p className="text-xs text-gray-600">{ac.title}</p>
                                <div className="flex items-center gap-2 mt-1">
                                  {ac.email && <span className="text-[10px] text-blue-600">{ac.email}</span>}
                                  {ac.linkedin_url && <a href={ac.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5"><Linkedin className="h-2.5 w-2.5" />LinkedIn</a>}
                                  {ac.city && <span className="text-[10px] text-gray-400">{ac.city}, {ac.state}</span>}
                                </div>
                              </div>
                              <div className="flex gap-1 ml-2">
                                {alreadyAdded ? (
                                  <span className="px-2 py-1 text-[10px] font-semibold text-green-600 bg-green-50 rounded-lg">Added</span>
                                ) : (
                                  <>
                                    <button onClick={() => revealAndAdd(ac)} disabled={revealingId === ac.apollo_id}
                                      className="px-2 py-1 text-[10px] font-semibold bg-[#D4AF37] text-[#0A1F44] rounded-lg hover:bg-[#D4AF37]/80 disabled:opacity-50"
                                      data-testid={`reveal-add-${i}`}>
                                      {revealingId === ac.apollo_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Reveal & Add'}
                                    </button>
                                    <button onClick={() => addApolloContactDirect(ac)}
                                      className="px-2 py-1 text-[10px] font-semibold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                                      data-testid={`add-direct-${i}`}>
                                      Add As-Is
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-gray-400">"Reveal & Add" uses 1 Apollo credit to get full name, email, and LinkedIn URL</p>
                    </div>
                  )}

                  {showAddDM && (
                    <div className="bg-gray-50 border rounded-xl p-4 space-y-3" data-testid="add-dm-form">
                      <div className="grid grid-cols-2 gap-3">
                        <input placeholder="Full Name *" value={dmForm.contact_name} onChange={e => setDmForm({ ...dmForm, contact_name: e.target.value })}
                          className="px-3 py-2 text-sm border rounded-lg" data-testid="dm-name-input" />
                        <input placeholder="Title *" value={dmForm.title} onChange={e => setDmForm({ ...dmForm, title: e.target.value })}
                          className="px-3 py-2 text-sm border rounded-lg" data-testid="dm-title-input" />
                        <input placeholder="Department" value={dmForm.department} onChange={e => setDmForm({ ...dmForm, department: e.target.value })}
                          className="px-3 py-2 text-sm border rounded-lg" data-testid="dm-dept-input" />
                        <input placeholder="Email" value={dmForm.email} onChange={e => setDmForm({ ...dmForm, email: e.target.value })}
                          className="px-3 py-2 text-sm border rounded-lg" data-testid="dm-email-input" />
                        <input placeholder="LinkedIn URL" value={dmForm.linkedin_url} onChange={e => setDmForm({ ...dmForm, linkedin_url: e.target.value })}
                          className="px-3 py-2 text-sm border rounded-lg" data-testid="dm-linkedin-input" />
                        <select value={dmForm.confidence_level} onChange={e => setDmForm({ ...dmForm, confidence_level: e.target.value })}
                          className="px-3 py-2 text-sm border rounded-lg" data-testid="dm-confidence-select">
                          {CONFIDENCE_LEVELS.map(l => <option key={l} value={l}>{l} Confidence</option>)}
                        </select>
                      </div>
                      <button onClick={addDecisionMaker} disabled={saving} data-testid="save-dm-btn"
                        className="px-4 py-2 text-xs font-semibold bg-[#D4AF37] text-[#0A1F44] rounded-lg hover:bg-[#D4AF37]/80 disabled:opacity-50">
                        {saving ? 'Saving...' : 'Save Contact'}
                      </button>
                    </div>
                  )}

                  <div className="space-y-2">
                    {dms.map((dm, i) => (
                      <div key={dm.id || i} className="bg-white border rounded-xl p-4 flex items-start justify-between hover:shadow-sm transition-all" data-testid={`dm-card-${i}`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-semibold text-[#0A1F44]">{dm.contact_name}</p>
                            <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${
                              dm.confidence_level === 'High' ? 'bg-green-100 text-green-700' :
                              dm.confidence_level === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                            }`}>{dm.confidence_level}</span>
                          </div>
                          <p className="text-xs text-gray-600">{dm.title}{dm.department ? ` · ${dm.department}` : ''}</p>
                          <div className="flex items-center gap-3 mt-2">
                            {dm.email && (
                              <a href={`mailto:${dm.email}`} className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                <Mail className="h-3 w-3" />{dm.email}
                              </a>
                            )}
                            {dm.linkedin_url && (
                              <a href={dm.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
                                <Linkedin className="h-3 w-3" />LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                        <button onClick={() => deleteDecisionMaker(dm.id)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-dm-${i}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {dms.length === 0 && !showAddDM && (
                      <div className="text-center py-8 text-gray-400">
                        <Users className="h-8 w-8 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No decision makers added yet</p>
                        <button onClick={() => setShowAddDM(true)} className="mt-2 text-xs text-[#D4AF37] hover:underline">Add your first contact</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Outreach & Timeline Tab */}
              {tab === 'outreach' && (
                <div className="space-y-4" data-testid="tab-outreach-content">
                  {/* Quick update */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 uppercase">Response Status</label>
                      <select value={outreachData?.response_status || 'No Response'} onChange={e => updateOutreach({ response_status: e.target.value })}
                        className="w-full px-3 py-2 text-sm border rounded-lg mt-1" data-testid="response-status-select">
                        {RESPONSE_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 uppercase">Next Follow-up</label>
                      <input type="date" value={outreachData?.next_followup_date || ''} onChange={e => updateOutreach({ next_followup_date: e.target.value })}
                        className="w-full px-3 py-2 text-sm border rounded-lg mt-1" data-testid="followup-date-input" />
                    </div>
                  </div>

                  {/* Add timeline note */}
                  <div className="bg-gray-50 border rounded-xl p-4 space-y-2" data-testid="add-note-form">
                    <p className="text-xs font-semibold text-[#0A1F44]">Log Activity</p>
                    <input placeholder="Action (e.g., Sent intro email, Left voicemail)" value={noteAction} onChange={e => setNoteAction(e.target.value)}
                      className="w-full px-3 py-2 text-sm border rounded-lg" data-testid="note-action-input" />
                    <textarea placeholder="Notes (optional)" value={noteText} onChange={e => setNoteText(e.target.value)} rows={2}
                      className="w-full px-3 py-2 text-sm border rounded-lg resize-none" data-testid="note-text-input" />
                    <button onClick={addTimelineNote} className="px-4 py-1.5 text-xs font-semibold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0A1F44]/80" data-testid="add-note-btn">
                      Add Entry
                    </button>
                  </div>

                  {/* Timeline */}
                  <div className="space-y-0">
                    <p className="text-xs font-semibold text-[#0A1F44] mb-3">Activity Timeline</p>
                    {timeline.length > 0 ? (
                      <div className="relative pl-6 border-l-2 border-gray-200 space-y-4">
                        {timeline.map((entry, i) => (
                          <div key={i} className="relative" data-testid={`timeline-entry-${i}`}>
                            <div className="absolute -left-[25px] top-1 w-3 h-3 rounded-full bg-[#D4AF37] border-2 border-white" />
                            <div className="bg-white border rounded-lg p-3">
                              <div className="flex items-center justify-between mb-1">
                                <p className="text-xs font-semibold text-[#0A1F44]">{entry.action}</p>
                                <p className="text-[10px] text-gray-400">{entry.created_at ? new Date(entry.created_at).toLocaleDateString() : ''}</p>
                              </div>
                              {entry.notes && <p className="text-xs text-gray-600">{entry.notes}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center py-6 text-gray-400 text-sm">No activity logged yet</p>
                    )}
                  </div>
                </div>
              )}

              {/* Pipeline Tab */}
              {tab === 'pipeline' && (
                <div className="space-y-4" data-testid="tab-pipeline-content">
                  <p className="text-xs font-semibold text-gray-500 uppercase">Sales Pipeline Stage</p>
                  <div className="flex gap-2 flex-wrap">
                    {PIPELINE_STAGES.map((stage, i) => {
                      const isActive = (outreachData?.pipeline_stage || 'Prospect') === stage;
                      const stageIndex = PIPELINE_STAGES.indexOf(outreachData?.pipeline_stage || 'Prospect');
                      const isPast = i < stageIndex;
                      return (
                        <button key={stage} onClick={() => updateOutreach({ pipeline_stage: stage })} data-testid={`pipeline-${stage.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex-1 min-w-[100px] px-3 py-3 text-xs font-semibold rounded-xl border-2 transition-all text-center ${
                            isActive
                              ? 'bg-[#0A1F44] text-white border-[#0A1F44] shadow-lg'
                              : isPast
                                ? 'bg-green-50 text-green-700 border-green-300'
                                : 'bg-white text-gray-500 border-gray-200 hover:border-[#D4AF37]'
                          }`}>
                          {isActive && <Check className="h-3 w-3 inline mr-1" />}
                          {stage}
                        </button>
                      );
                    })}
                  </div>

                  <div className="bg-gray-50 border rounded-xl p-4 space-y-3 mt-4">
                    <p className="text-xs font-semibold text-[#0A1F44]">Outreach Details</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-semibold text-gray-500 uppercase">Last Outreach Date</label>
                        <input type="date" value={outreachData?.last_outreach_date || ''} onChange={e => updateOutreach({ last_outreach_date: e.target.value })}
                          className="w-full px-3 py-2 text-sm border rounded-lg mt-1" data-testid="last-outreach-date" />
                      </div>
                      <div>
                        <label className="text-[10px] font-semibold text-gray-500 uppercase">Next Follow-up</label>
                        <input type="date" value={outreachData?.next_followup_date || ''} onChange={e => updateOutreach({ next_followup_date: e.target.value })}
                          className="w-full px-3 py-2 text-sm border rounded-lg mt-1" data-testid="pipeline-followup-date" />
                      </div>
                    </div>
                    <div>
                      <label className="text-[10px] font-semibold text-gray-500 uppercase">Notes</label>
                      <textarea rows={2} defaultValue={outreachData?.notes || ''} onBlur={e => { if (e.target.value !== (outreachData?.notes || '')) updateOutreach({ notes: e.target.value }); }}
                        className="w-full px-3 py-2 text-sm border rounded-lg mt-1 resize-none" data-testid="pipeline-notes" placeholder="Add notes about this prospect..." />
                    </div>
                  </div>
                </div>
              )}

              {/* AI Messages Tab */}
              {tab === 'messages' && (
                <div className="space-y-4" data-testid="tab-messages-content">
                  {!aiMessages && !loadingMessages && (
                    <div className="text-center py-8">
                      <Wand2 className="h-10 w-10 mx-auto text-[#D4AF37] mb-3" />
                      <p className="text-sm font-semibold text-[#0A1F44] mb-1">Generate Outreach Messages</p>
                      <p className="text-xs text-gray-500 mb-4">AI will analyze hiring signals and create personalized messages</p>
                      <button onClick={generateMessages} data-testid="generate-messages-btn"
                        className="px-6 py-2.5 text-sm font-semibold bg-[#D4AF37] text-[#0A1F44] rounded-xl hover:bg-[#D4AF37]/80 transition-all">
                        Generate Outreach Messages
                      </button>
                    </div>
                  )}
                  {loadingMessages && (
                    <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-[#D4AF37]" /><span className="ml-2 text-sm text-gray-500">Analyzing signals...</span></div>
                  )}
                  {aiMessages && (
                    <>
                      {/* Signals Detected */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                        <p className="text-[10px] font-semibold text-blue-700 uppercase mb-1">{aiMessages.signal_count} Signals Detected</p>
                        <div className="flex flex-wrap gap-1">
                          {(aiMessages.signals_detected || []).map((s, i) => (
                            <span key={i} className="px-2 py-0.5 text-[10px] bg-white text-blue-700 rounded-full border border-blue-200">{s}</span>
                          ))}
                        </div>
                      </div>

                      {/* Message Cards */}
                      {[
                        { key: 'linkedin_connection', label: 'LinkedIn Connection', icon: Linkedin, color: 'border-blue-500' },
                        { key: 'linkedin_followup', label: 'LinkedIn Follow-Up', icon: Linkedin, color: 'border-indigo-500' },
                        { key: 'email_outreach', label: 'Email Outreach', icon: Mail, color: 'border-amber-500' },
                      ].map(msg => (
                        <div key={msg.key} className={`bg-white border rounded-xl overflow-hidden border-l-4 ${msg.color}`} data-testid={`msg-${msg.key}`}>
                          <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <msg.icon className="h-4 w-4 text-gray-500" />
                              <span className="text-xs font-semibold text-[#0A1F44]">{msg.label}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button onClick={() => copyText(aiMessages[msg.key])} data-testid={`copy-${msg.key}`}
                                className="px-2 py-1 text-[10px] font-semibold bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 flex items-center gap-1">
                                <Copy className="h-3 w-3" /> Copy
                              </button>
                              <button onClick={() => logMessageToTimeline(msg.label)} data-testid={`log-${msg.key}`}
                                className="px-2 py-1 text-[10px] font-semibold bg-[#0A1F44] text-white rounded-lg hover:bg-[#0A1F44]/80">
                                Log to Timeline
                              </button>
                            </div>
                          </div>
                          <div className="p-3">
                            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed">{aiMessages[msg.key]}</pre>
                          </div>
                        </div>
                      ))}

                      <button onClick={generateMessages} className="w-full py-2 text-xs text-gray-500 hover:text-[#0A1F44] transition-colors" data-testid="regenerate-btn">
                        Regenerate Messages
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailModal;
